import React, { useState, useMemo } from 'react';
import Icon from "../icons/Icon";
import { format } from 'date-fns';
import { expandableRowProps } from '../../utils/expandableRow';
import { parsePeriodToDate, toYMDString } from '../../utils/periodDate';
import { buildPayComposition } from '../../utils/payrollCalculations';
import { matchesEmployeeSearch } from '../../utils/employeeSearch';
import { PHILIPPINE_LABOR_LAW_MINIMUMS } from '../../constants/laborLawMinimums';

// Hours render to two decimals everywhere in this component, so a column and its own
// total always agree — and the raw float sum can never surface (1.1 + 2.2 adds to
// 3.3000000000000003, which is what the totals row used to print).
const formatHours = (h) => (Number(h) || 0).toFixed(2);

// Statutory floors, for the case where a rate is missing or stored as 0. The literals
// live in laborLawMinimums.js, so a future law revision reaches these too.
const LAW = PHILIPPINE_LABOR_LAW_MINIMUMS.rates;

/**
 * GrossIncomeBreakdown - Step 3 of Payroll Process
 * Displays gross income breakdown for all employees including:
 * - Basic pay (daily rate + piece rate)
 * - Overtime pay (daily breakdown)
 * - Night differential pay (daily breakdown)
 * - Holiday pay
 */
// PRD_GrossIncomeRedesign C7 — each money column is a fixed group of the
// composition's allocated lines (whole centavos). Every line the model can emit
// belongs to exactly one column, so a row's columns add to its Gross exactly,
// and the run totals strip, summed the same way, adds to its Grand Total.
const COLUMN_KEYS = {
  basic: ['basic', 'pieceRate'],
  otNd: ['overtime', 'nightDiff', 'nightDiffOt'],
  restHoliday: ['restDay', 'holiday'],
  lessTime: ['late', 'undertime', 'absence'],
};

const columnCents = (employee) => {
  const lines = buildPayComposition(employee).lines;
  const cents = { gross: 0 };
  Object.entries(COLUMN_KEYS).forEach(([column, keys]) => {
    cents[column] = lines
      .filter((line) => keys.includes(line.key))
      .reduce((sum, line) => sum + Math.round(line.amount * 100), 0);
    cents.gross += cents[column];
  });
  return cents;
};

const GrossIncomeBreakdown = ({
  employees,
  periodStart,
  periodEnd,
  companySettings, // eslint-disable-line no-unused-vars
  payrollRates,
  attendancePolicy,
  computedRates
}) => {
  // The rates the SERVER computed with, when it has told us; live settings
  // otherwise. Only the legacy OT/ND fallback reads them (PRD_GrossIncomeRedesign
  // C12 — no rate is printed on this step).
  const rates = computedRates || payrollRates;
  // State for expanded employee rows
  const [expandedEmployeeId, setExpandedEmployeeId] = useState(null);

  // Search & Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  // Sorting state
  const [sortField, setSortField] = useState('employeeName');
  const [sortDirection, setSortDirection] = useState('asc');

  // Run totals strip (C7): the column groups summed in centavos over every
  // employee in the run (unfiltered), so the four add to Grand Total exactly.
  const footerTotals = useMemo(() => {
    const totals = { basic: 0, otNd: 0, restHoliday: 0, lessTime: 0, gross: 0 };
    (employees || []).forEach((emp) => {
      const cents = columnCents(emp);
      Object.keys(totals).forEach((key) => { totals[key] += cents[key]; });
    });
    return totals;
  }, [employees]);

  // Get unique departments for filter dropdown
  const departments = useMemo(() => {
    if (!employees || employees.length === 0) return [];
    return [...new Set(employees.map(emp => emp.employment?.department).filter(Boolean))].sort();
  }, [employees]);

  // Get employee initials for avatar (FirstName initial + LastName initial)
  const getInitials = (employee) => {
    const first = employee.personalInfo?.firstName?.charAt(0) || '';
    const last = employee.personalInfo?.lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase() || '??';
  };

  // Get employee full name - Format: "Last Name, First Name" for consistent sorting
  const getFullName = (employee) => {
    const firstName = employee.personalInfo?.firstName || '';
    const lastName = employee.personalInfo?.lastName || '';
    if (lastName && firstName) {
      return `${lastName}, ${firstName}`;
    }
    return employee.personalInfo?.fullName || lastName || firstName || 'Unknown';
  };

  // Calculate daily rate (display) — prefer the engine-resolved value from the
  // strategy; the monthly derivation uses dailyRateFactor (C1 — the old /260
  // disagreed with the engine and is deleted, C5).
  const calculateDailyRate = (employee) => {
    if (typeof employee.dailyRate === 'number') return employee.dailyRate;
    if (employee.employment?.salaryType === 'daily') {
      return parseFloat(employee.employment?.dailySalary) || 0;
    }
    const monthlySalary = parseFloat(employee.employment?.monthlySalary) || 0;
    const factor = payrollRates?.dailyRateFactor || 365;
    return monthlySalary > 0 ? (monthlySalary * 12) / factor : 0;
  };

  // Get daily breakdown data for an employee
  const getDailyBreakdown = (employee) => {
    const timeRecords = employee.timeRecords || [];
    const dailyRate = calculateDailyRate(employee);
    // Mirror the engine's resolution exactly (ProcessPayroll.js ratesUsed):
    // attendancePolicy first — that field's home — then payrollRates pre-backfill.
    // `||` not `??` throughout, matching the ND precedent below: a stored 0 is an
    // invalid divisor and must coerce to the default, not produce Infinity.
    const hoursPerDay = computedRates?.hoursPerDay ||
      attendancePolicy?.hoursPerDay || payrollRates?.hoursPerDay || 8;
    const hourlyRate = dailyRate / hoursPerDay;

    const isMonthly = employee.employment?.salaryType === 'monthly';
    const hasOvertimeDetails = Array.isArray(employee.earnings?.overtimeDetails);
    const hasNightDiffDetails = Array.isArray(employee.earnings?.nightDiffDetails);

    // Create a lookup map for overtimeDetails by date (pre-calculated with compound multipliers)
    const overtimeDetailsByDate = {};
    (employee.earnings?.overtimeDetails || []).forEach(detail => {
      overtimeDetailsByDate[detail.date] = detail;
    });

    // Same for nightDiffDetails (BL-086) — the engine now day-types ND per record,
    // so the per-day figure must come from the engine's own row rather than being
    // recomputed here at a flat rate.
    const nightDiffDetailsByDate = {};
    (employee.earnings?.nightDiffDetails || []).forEach(detail => {
      nightDiffDetailsByDate[detail.date] = detail;
    });

    // Create a lookup map for restDayDetails by date (pre-calculated in ProcessPayroll)
    const restDayDetailsByDate = {};
    (employee.earnings?.restDayDetails || []).forEach(detail => {
      restDayDetailsByDate[detail.date] = detail;
    });

    // A worked holiday-on-rest-day gets NO restDayDetails row (its premium lives
    // in holidayDetails), so the rest-day marker for that case must come from the
    // holiday detail's isOnRestDay flag — same signal the Holiday Pay section
    // renders as "+ Rest Day" (BL-036a).
    const holidayDetailsByDate = {};
    (employee.earnings?.holidayDetails || []).forEach(detail => {
      holidayDetailsByDate[detail.date] = detail;
    });

    // Per-day absence dock from the engine (monthly deduct-down; daily is always
    // 0 — B3). The engine is the single source of truth for whether a day docked
    // (it already applies the scheduled-workday / holiday / rest exclusions), so
    // the row shows exactly what was subtracted rather than re-deriving it.
    const absenceByDate = {};
    (employee.absenceDetails || []).forEach(detail => {
      absenceByDate[detail.date] = detail.amount;
    });

    // Sort by date - include worked days, paid/unpaid leave, and explicit absences.
    // Absences are managed on the Time Tracking page (isAbsent DTR records); the
    // wizard only reads them and surfaces them here as deduct-down edges.
    return timeRecords
      .filter(record => {
        // Include if: worked (has timeIn/timeOut and not on leave) OR any leave
        // day OR an explicit absence. Every explicit Absent DTR is shown for both
        // pay types (it's a deliberate record, unlike the no-record "presumed
        // paid" gaps): monthly docks a day; daily never docks (earn-up).
        const isWorkedDay = record.onLeave !== "yes" && record.timeIn && record.timeOut;
        const isPaidLeaveDay = record.onLeave === "yes" && record.isPaidLeave === true;
        const isUnpaidLeaveDay = record.onLeave === "yes" && record.isPaidLeave !== true;
        const isAbsentDay = record.isAbsent === true;
        return isWorkedDay || isPaidLeaveDay || isUnpaidLeaveDay || isAbsentDay;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(record => {
        const date = parsePeriodToDate(record.date);
        const isPieceRate = record.isPieceRate || false;
        const isOnLeave = record.onLeave === "yes";
        const isPaidLeave = isOnLeave && record.isPaidLeave === true;
        const isUnpaidLeave = isOnLeave && record.isPaidLeave !== true;
        const isAbsent = record.isAbsent === true;

        // Rest day comes from the engine's restDayDetails.
        const restDayDetail = restDayDetailsByDate[record.date];
        // The day-build stamps record.isHoliday only on scheduled WORKING days, so a
        // worked holiday-on-rest-day record carries no holiday flags — the holiday
        // detail is the only signal for this case.
        const holidayDetail = holidayDetailsByDate[record.date];
        const isHolidayOnRestDay = !!(holidayDetail?.workedOnHoliday && holidayDetail?.isOnRestDay);

        // Per-day absence dock from the engine (0 → nothing subtracted, e.g. a
        // daily employee, or a holiday/rest day the engine excludes).
        const absenceDock = absenceByDate[record.date] || 0;

        // PRD_GrossIncomeRedesign — the day model: only the lines that apply, each an
        // engine figure or the per-day figure this screen already showed. A receipt
        // carries NO total (C5): nothing stores a per-day gross to foot it to.
        const lines = [];
        if (isAbsent || isUnpaidLeave) {
          if (absenceDock > 0) lines.push({ label: 'Less: Absence', amount: -absenceDock });
        } else if (isPaidLeave) {
          if (!isMonthly) lines.push({ label: 'Paid leave', amount: dailyRate });
        } else if (isPieceRate && !isMonthly) {
          // Only the daily engine pays piece rate; a monthly piece-rate day falls
          // through to its rest-day premium, if any.
          lines.push({ label: 'Piece pay', amount: record.pieceRateValue || 0 });
        } else if (restDayDetail) {
          // Monthly's regularPay is 0 — the base is already inside the salary.
          if (restDayDetail.regularPay) lines.push({ label: 'Base day', amount: restDayDetail.regularPay });
          lines.push({ label: 'Rest day premium', amount: restDayDetail.premium });
        } else if (!isMonthly) {
          lines.push({ label: 'Base day', amount: dailyRate });
        }
        // holidayPay is the premium only (the base is in earnings.basic). A regular
        // or special holiday on a rest day is one engine amount and writes no
        // restDayDetails row.
        if (holidayDetail?.workedOnHoliday && holidayDetail.holidayPay) {
          lines.push({ label: 'Holiday premium', amount: holidayDetail.holidayPay });
        }

        if (!isOnLeave && !isAbsent) {
          // OT/ND are the engine's per-day rows. The legacy fallback prices a run saved
          // before the engine wrote those arrays — never a date missing from a present
          // array, which is a day the engine did not pay (a fixed-basis monthly
          // employee's overtime, for one). It reads the CONFIGURED rate, server first,
          // rather than a hardcoded 1.25 / 0.10 (BL-095/BL-095b).
          const otDetail = overtimeDetailsByDate[record.date];
          const overtimeHours = record.overtimeHours || otDetail?.hours || 0;
          const configuredOtRate = rates?.overtimeRate || LAW.overtimeRate;
          const overtimePay = otDetail
            ? otDetail.pay
            : (hasOvertimeDetails ? 0 : overtimeHours * hourlyRate * configuredOtRate);
          if (overtimePay > 0) {
            lines.push({ label: 'Overtime', qty: `${formatHours(overtimeHours)}h`, amount: overtimePay });
          }

          // BL-086: the engine's day-typed ND pay. `||` not `??`, matching all five
          // engines: a stored 0 is an invalid rate and must coerce to the default.
          const ndDetail = nightDiffDetailsByDate[record.date];
          const nightDiffHours = record.nightDiffHours || ndDetail?.hours || 0;
          const configuredNdRate = rates?.nightDiffRate || LAW.nightDiffRate;
          const nightDiffPay = ndDetail
            ? ndDetail.pay
            : (hasNightDiffDetails ? 0 : nightDiffHours * hourlyRate * configuredNdRate);
          const ndOtHours = ndDetail?.ndOtHours || 0;
          if (nightDiffPay > 0 && ndOtHours > 0) {
            // PRD_ShiftRules A2: the night hours that are also OT print as their own line
            // (10% of the OT rate). Split in centavos so the pair adds to the day's
            // rounded pay; the plain line is skipped when every night hour is OT.
            const otCents = Math.round((ndDetail.ndOtPay || 0) * 100);
            const plainHours = Math.round((ndDetail.hours - ndOtHours) * 100) / 100;
            if (plainHours > 0) {
              lines.push({ label: 'Night differential', qty: `${formatHours(plainHours)}h`, amount: (Math.round(nightDiffPay * 100) - otCents) / 100 });
            }
            lines.push({ label: 'Night differential on OT', qty: `${formatHours(ndOtHours)}h`, amount: otCents / 100 });
          } else if (nightDiffPay > 0) {
            lines.push({ label: 'Night differential', qty: `${formatHours(nightDiffHours)}h`, amount: nightDiffPay });
          }

          // The engine computes undertime only for the period, so this is the one
          // carried-over display figure — shown only when undertime was deducted.
          // PRD_CbaPremiumBase C7: prefer the rate the ENGINE deducted at (`??`, so a
          // stamped 0 is kept); monthly does not stamp it, so it falls back here.
          const undertimeHours = record.undertimeHours || 0;
          if (undertimeHours > 0 && (employee.deductions?.undertime || 0) > 0) {
            lines.push({
              label: 'Less: Undertime',
              qty: `${formatHours(undertimeHours)}h`,
              amount: -(undertimeHours * (employee.undertimeHourlyRate ?? hourlyRate)),
            });
          }
        }

        const [badge, badgeClass] =
          isAbsent ? ['Absent', 'absent'] :
          isUnpaidLeave ? ['Unpaid leave', 'leave-u'] :
          isPaidLeave ? ['Paid leave', 'leave-p'] :
          isPieceRate ? ['Piece', 'piece'] :
          isHolidayOnRestDay ? ['Holiday + Rest', 'holiday'] :
          restDayDetail ? ['Rest', 'rest'] :
          record.isHoliday ? ['Holiday', 'holiday'] : ['Daily', 'daily'];

        return {
          date: record.date,
          dayName: format(date, 'EEE'),
          // 'YYYY-MM-DD' — the app-wide standard (DESIGN-STANDARDS.md §13).
          dateFormatted: toYMDString(date),
          badge,
          badgeClass,
          lines,
        };
      });
  };

  // Toggle expanded row
  const toggleExpansion = (employeeId) => {
    setExpandedEmployeeId(expandedEmployeeId === employeeId ? null : employeeId);
  };

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort Icon Component
  const SortIcon = ({ field }) => {
    const isActive = sortField === field;
    const color = "white";

    if (isActive && sortDirection === "asc") {
      return (
        <Icon name="arrow-up" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    if (isActive && sortDirection === "desc") {
      return (
        <Icon name="arrow-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    return (
      <Icon name="chevrons-up-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle", opacity: 0.5 }} />
    );
  };

  // Filter and sort employees
  const sortedEmployees = useMemo(() => {
    if (!employees || employees.length === 0) return [];

    let filtered = [...employees];

    // Apply department filter
    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(emp => emp.employment?.department === selectedDepartment);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(emp => matchesEmployeeSearch(emp, searchTerm));
    }

    return filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employeeName':
          // Sort by lastName first, then firstName as secondary sort
          aValue = (a.personalInfo?.lastName || '').toLowerCase();
          bValue = (b.personalInfo?.lastName || '').toLowerCase();
          if (aValue === bValue) {
            aValue = (a.personalInfo?.firstName || '').toLowerCase();
            bValue = (b.personalInfo?.firstName || '').toLowerCase();
          }
          break;
        // Sorted by the figure each column shows (C7).
        case 'basicPay':
          aValue = columnCents(a).basic;
          bValue = columnCents(b).basic;
          break;
        case 'grossTotal':
          aValue = columnCents(a).gross;
          bValue = columnCents(b).gross;
          break;
        default:
          aValue = (a.personalInfo?.lastName || '').toLowerCase();
          bValue = (b.personalInfo?.lastName || '').toLowerCase();
      }

      if (typeof aValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [employees, sortField, sortDirection, searchTerm, selectedDepartment]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (!employees || employees.length === 0) {
    return (
      <div className="gross-income-breakdown">
        <div className="gross-income-empty">
          <p>No employees found for this payroll period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gross-income-breakdown">
      {/* Main Content */}
      <div className="gross-income-content">
        <div className="content-header">
          <div className="pw-header">
            <h2 className="pw-header__title">Gross Income Breakdown</h2>
            {periodStart && periodEnd && (
              <span className="pw-header__badge">
                {format(parsePeriodToDate(periodStart), 'MMM d, yyyy')} – {format(parsePeriodToDate(periodEnd), 'MMM d, yyyy')}
              </span>
            )}
          </div>
        </div>

        {/* Search & Filter Controls */}
        <div className="pw-toolbar">
          <div className="pw-toolbar__controls">
            <input
              type="text"
              className="pw-toolbar__search"
              placeholder="Search by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="pw-toolbar__select"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          <span className="pw-toolbar__count">
            Showing {sortedEmployees.length} of {employees.length} employees
          </span>
        </div>

        {/* Employee Table */}
        <div className="pw-table-scroll">
        <table className="pw-table">
          <thead>
            <tr>
              <th className="sortable-header" onClick={() => handleSort('employeeName')}>
                Employee<SortIcon field="employeeName" />
              </th>
              <th className="text-right sortable-header" onClick={() => handleSort('basicPay')}>
                Basic<SortIcon field="basicPay" />
              </th>
              <th className="text-right">OT &amp; Night Diff</th>
              <th className="text-right">Rest Day &amp; Holiday</th>
              <th className="text-right">Less: Time</th>
              <th className="text-right sortable-header" onClick={() => handleSort('grossTotal')}>
                Gross<SortIcon field="grossTotal" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEmployees.map((employee) => {
              const isExpanded = expandedEmployeeId === employee.id;
              const cents = columnCents(employee);
              const dailyBreakdown = getDailyBreakdown(employee);
              const composition = buildPayComposition(employee);
              // Monthly is deduct-down: a worked holiday adds only the premium (the
              // base day is already in the salary), so its holiday entry says so.
              const isMonthlyEmp = employee.employment?.salaryType === 'monthly';

              return (
                <React.Fragment key={employee.id}>
                  {/* Main Employee Row */}
                  <tr
                    className={`employee-row ${isExpanded ? 'expanded' : ''}`}
                    {...expandableRowProps(isExpanded, () => toggleExpansion(employee.id))}
                  >
                    <td className="pw-name-cell">
                      <div className="pw-name-cell__inner">
                        <div className="pw-avatar">{getInitials(employee)}</div>
                        <div>
                          <span className="pw-name-cell__text">{getFullName(employee)}</span>
                          <div className="gib-name-meta">
                            {[employee.employeeId || employee.id, employee.employment?.department].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <Icon name="chevron-down" size={12} className={`pw-chevron ${isExpanded ? 'expanded' : ''}`} />
                      </div>
                    </td>
                    <td className="text-right">
                      <span className="amount">{formatCurrency(cents.basic / 100)}</span>
                    </td>
                    <td className="text-right">
                      <span className={`amount ${cents.otNd === 0 ? 'amount--zero' : ''}`}>
                        {formatCurrency(cents.otNd / 100)}
                      </span>
                    </td>
                    <td className="text-right">
                      <span className={`amount ${cents.restHoliday === 0 ? 'amount--zero' : ''}`}>
                        {formatCurrency(cents.restHoliday / 100)}
                      </span>
                    </td>
                    <td className="text-right">
                      <span className={`amount ${cents.lessTime === 0 ? 'amount--zero' : 'amount--negative'}`}>
                        {formatCurrency(cents.lessTime / 100)}
                      </span>
                    </td>
                    <td className="text-right">
                      <span className="amount amount--highlight">
                        {formatCurrency(cents.gross / 100)}
                      </span>
                    </td>
                  </tr>

                  {/* Expansion Row */}
                  {isExpanded && (
                    <tr className="expansion-row">
                      <td colSpan="6">
                        <div className="expansion-content">
                          <div className="expansion-grid">
                            {/* Pay Breakdown Section */}
                            <div className="expansion-section">
                              <div className="expansion-section__title">
                                <span className="icon"><Icon name="clock" size={16} /></span> Pay Breakdown
                              </div>

                              {/* C3 — buildPayComposition rendered as the Step-6 Gross card
                                  renders it: Basic always, every other line only when it
                                  carries money. The lines foot to Gross by construction (C6b). */}
                              <ul className="gib-composition" aria-label="Gross composition">
                                {composition.lines
                                  .filter((line) => line.key === 'basic' || !line.isZero)
                                  .map((line) => (
                                    <li key={line.key} className="gib-composition__line">
                                      <span>
                                        {line.kind === 'timeDeduction' ? `Less: ${line.label}` : line.label}
                                        {line.qty ? ` (${line.qty})` : ''}
                                      </span>
                                      <span className={line.amount < 0 ? 'gib-composition__amount--negative' : ''}>
                                        {formatCurrency(line.amount)}
                                      </span>
                                    </li>
                                  ))}
                                <li className="gib-composition__line gib-composition__line--total">
                                  <span>Gross</span>
                                  <span>{formatCurrency(composition.gross)}</span>
                                </li>
                              </ul>

                              {/* Monthly is deduct-down: the base is the fixed salary,
                                  never a sum of the days. The block above prints the
                                  base and any absences. */}
                              {isMonthlyEmp && (
                                <div className="gib-monthly-base">
                                  <span className="gib-monthly-base__note">
                                    {employee.employment?.payBasis === 'attendance'
                                      ? 'Days below only add premiums or subtract time — they never build the base.'
                                      : 'Fixed pay basis — the days below are attendance only and do not change this pay.'}
                                  </span>
                                </div>
                              )}

                              {dailyBreakdown.length > 0 ? (
                                <>
                                  <div className="gib-days__title">Days</div>
                                  <ul className="gib-days" aria-label="Days">
                                    {dailyBreakdown.map((day, index) => (
                                      <li key={`${day.date}-${index}`} className="gib-day" aria-label={day.dateFormatted}>
                                        <div className="gib-day__header">
                                          <span className="date-day">{day.dayName}</span>
                                          <span className="date-full">{day.dateFormatted}</span>
                                          <span className={`type-badge type-badge--${day.badgeClass}`}>{day.badge}</span>
                                        </div>
                                        {day.lines.map((line) => (
                                          <div key={line.label} className="gib-day__line">
                                            <span>{line.qty ? `${line.label} (${line.qty})` : line.label}</span>
                                            <span className={line.amount < 0 ? 'gib-day__amount--negative' : ''}>
                                              {formatCurrency(line.amount)}
                                            </span>
                                          </div>
                                        ))}
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              ) : (
                                <p className="no-data">No daily records available for this period.</p>
                              )}
                            </div>

                            {/* Holiday Pay Section */}
                            <div className="expansion-section">
                              <div className="expansion-section__title">
                                <span className="icon"><Icon name="sparkles" size={16} /></span> Holiday Pay
                              </div>

                              {employee.earnings?.holidayDetails?.length > 0 ? (
                                <>
                                  {employee.earnings.holidayDetails.map((holiday, index) => (
                                    <div key={index} className={`holiday-item ${!holiday.isEntitled && !holiday.workedOnHoliday ? 'holiday-item--not-entitled' : ''}`}>
                                      <div className="holiday-info">
                                        <span className="holiday-name">{holiday.name}</span>
                                        <span className="holiday-date">
                                          {format(new Date(holiday.date), 'MMM d, yyyy')}
                                        </span>
                                      </div>
                                      <div className="holiday-status">
                                        {holiday.type === 'company' ? (
                                          <span className="holiday-type holiday-type--company">No Holiday Pay</span>
                                        ) : holiday.workedOnHoliday ? (
                                          <>
                                            <span className={`holiday-type holiday-type--${holiday.type}`}>
                                              {isMonthlyEmp ? 'Worked (extra pay)' : 'Worked'}
                                              {holiday.isOnRestDay && ' + Rest Day'}
                                            </span>
                                            <span className="holiday-pay">{formatCurrency(holiday.holidayPay)}</span>
                                            {isMonthlyEmp && (
                                              <span className="holiday-premium-note">
                                                The day is already paid in the salary — this is just the extra for working it.
                                              </span>
                                            )}
                                          </>
                                        ) : holiday.isEntitled ? (
                                          <>
                                            <span className={`holiday-type holiday-type--${holiday.type}`}>
                                              {/* Special days are "no work, no pay" (₱0). (BL-036b) */}
                                              {holiday.type === 'special'
                                                ? 'Did not work (no pay)'
                                                : 'Did not work'}
                                            </span>
                                            <span className="holiday-pay">{formatCurrency(holiday.holidayPay)}</span>
                                          </>
                                        ) : (
                                          <div className="holiday-not-entitled">
                                            <span className="holiday-not-entitled__badge">Not Entitled</span>
                                            <span className="holiday-not-entitled__reason" title={holiday.eligibilityReason}>
                                              {holiday.eligibilityReason}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <p className="no-data">No holidays in this period</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>

        {/* Table Footer */}
        <div className="pw-footer">
          <div className="pw-footer__stats">
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Basic</span>
              <span className="pw-footer__stat-value">{formatCurrency(footerTotals.basic / 100)}</span>
            </div>
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">OT &amp; Night Diff</span>
              <span className="pw-footer__stat-value">{formatCurrency(footerTotals.otNd / 100)}</span>
            </div>
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Rest Day &amp; Holiday</span>
              <span className="pw-footer__stat-value">{formatCurrency(footerTotals.restHoliday / 100)}</span>
            </div>
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Less: Time</span>
              <span className={`pw-footer__stat-value${footerTotals.lessTime < 0 ? ' pw-footer__stat-value--negative' : ''}`}>
                {formatCurrency(footerTotals.lessTime / 100)}
              </span>
            </div>
            <div className="pw-footer__stat pw-footer__stat--total">
              <span className="pw-footer__stat-label">Grand Total</span>
              <span className="pw-footer__stat-value">{formatCurrency(footerTotals.gross / 100)}</span>
            </div>
          </div>
          <span className="pw-footer__hint">Click on any row to expand</span>
        </div>
      </div>
    </div>
  );
};

export default GrossIncomeBreakdown;
